<template>
  <div
    class="ndsInputCheckbox"
    :class="{
      [`-${size}`]: true,
    }"
    :data-selector="`checkbox-${dataSelector}`"
    @click.stop="emit('click', $event)"
  >
    <input
      :id="`nds-input-checkbox-${uid}`"
      ref="checkbox"
      :aria-labelledby="`nds-checkbox-label-${uid}`"
      :checked="isChecked"
      data-testid="nds-checkbox-input"
      :disabled="disabled"
      type="checkbox"
      :value="option"
      @click.stop
      @input="check"
    />
    <label
      :data-selector="dataSelector"
      data-testid="nds-checkbox-label"
      :for="`nds-input-checkbox-${uid}`"
      :tabindex="disabled || !isFocusable ? -1 : 0"
      @keydown.space.prevent
      @keyup.enter="click"
      @keyup.space="click"
    >
      <span class="ndsInputCheckbox__check">
        <NdsIcon
          :icon="NdsIconType.CHECK"
          :size="iconSize"
        />
      </span>
      <slot />
    </label>
  </div>
</template>

<script setup lang="ts">
import { computed, getCurrentInstance, ref, type PropType } from 'vue'
import NdsIcon from '@/components/NewDesignSystem/NdsIcon/NdsIcon.vue'
import { NdsIconType } from '@/components/NewDesignSystem/NdsIcon/NdsIcon.interfaces'
import { NdsInputCheckboxSize } from '@/components/NewDesignSystem/NdsInputCheckbox/NdsInputCheckbox.interfaces'

const instance = getCurrentInstance()?.proxy
const uid = instance && '_uid' in instance ? instance['_uid'] : 'default'

const props = defineProps({
  dataSelector: {
    type: String,
    required: false,
    default: 'input-checkbox',
  },
  option: {
    type: [String, Number],
    required: true,
  },
  value: {
    type: [Boolean, String, Array],
    required: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  isFocusable: {
    type: Boolean,
    default: true,
  },
  size: {
    type: String as PropType<NdsInputCheckboxSize>,
    default: NdsInputCheckboxSize.M,
  },
})

const checkbox = ref<HTMLInputElement | null>(null)
const iconSize = ref<number>(props.size === NdsInputCheckboxSize.S ? 14 : 20)

const isChecked = computed(() => {
  if (Array.isArray(props.value)) {
    return props.value.includes(props.option)
  }

  if (typeof props.value === 'string') {
    return props.value === props.option
  }

  return props.value
})

const emit = defineEmits(['input', 'click'])

const click = () => {
  checkbox.value?.click()
}

const check = (event: { target: HTMLInputElement }) => {
  if (Array.isArray(props.value)) {
    const values = [...props.value]
    const i = values.indexOf(props.option)

    if (i < 0 && event.target.checked) {
      emit('input', [...values, props.option])
    }
    if (i >= 0 && !event.target.checked) {
      values.splice(i, 1)
      emit('input', values)
    }
  } else if (typeof props.value === 'boolean') {
    emit('input', event.target.checked)
  } else {
    emit('input', event.target.checked ? props.option : void 0)
  }
}
</script>

<style lang="postcss" scoped>
.ndsInputCheckbox {
  height: 24px;
  display: flex;
  align-items: center;
  gap: var(--spacing-1);

  &.-small {
    height: 16px;
    input[type='checkbox'] + label {
      padding-left: 24px;

      &::before {
        width: 16px;
        height: 16px;
        border-radius: 4px;
      }

      &::after {
        width: 14px;
        height: 14px;
        border-radius: 3px;
        left: 1px;
      }

      .ndsInputCheckbox__check {
        left: 1px;
      }
    }
  }

  input[type='checkbox'] {
    display: none;
  }

  input[type='checkbox'] + label {
    position: relative;
    padding-left: 32px;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 16px;
    cursor: pointer;
    margin: 0;

    &::before {
      content: '';
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      left: 0;
      width: 24px;
      height: 24px;
      border-radius: 7px;
      @apply tw-bg-transparent;
    }

    &::after {
      content: '';
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      left: 2px;
      width: 20px;
      height: 20px;
      border-radius: 5px;
      @apply tw-bg-white;
      border-width: 1px;
      border-style: solid;
      @apply tw-border-neutral-300;
    }

    .ndsInputCheckbox__check {
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      left: 2px;
      z-index: 1;

      &::v-deep .nds-icon {
        stroke-width: 2px !important;
        @apply !tw-stroke-white;
      }
    }

    &:hover {
      &::after {
        @apply tw-bg-neutral-50;
        @apply tw-border-neutral-300;
      }

      .ndsInputCheckbox__check {
        &::v-deep .nds-icon {
          @apply !tw-stroke-neutral-50;
        }
      }
    }
  }

  input[type='checkbox']:checked + label {
    &::after {
      @apply tw-bg-primary-500;
      @apply tw-border-primary-500;
    }

    &:hover {
      &:after {
        @apply tw-bg-primary-600;
        @apply tw-border-primary-600;
      }

      .ndsInputCheckbox__check {
        &::v-deep .nds-icon {
          @apply !tw-stroke-white;
        }
      }
    }
  }

  input[type='checkbox'] + label:focus {
    outline: none;

    &:before {
      @apply tw-bg-primary-100;
    }

    &::after {
      @apply tw-bg-primary-100;
      @apply tw-border-primary-600;
    }

    .ndsInputCheckbox__check {
      &::v-deep .nds-icon {
        @apply !tw-stroke-primary-100;
      }
    }
  }

  input[type='checkbox']:checked + label:focus {
    outline: none;

    &::before {
      @apply tw-bg-primary-100;
    }

    &::after {
      @apply tw-bg-primary-500;
      @apply tw-border-primary-500;
    }

    .ndsInputCheckbox__check {
      &::v-deep .nds-icon {
        @apply !tw-stroke-white;
      }
    }
  }

  input[type='checkbox']:disabled + label {
    cursor: not-allowed;

    &::before {
      @apply tw-bg-white;
    }

    &::after {
      @apply tw-bg-neutral-200;
      @apply tw-border-neutral-200;
    }

    .ndsInputCheckbox__check {
      &::v-deep .nds-icon {
        @apply !tw-stroke-neutral-200;
      }
    }
  }

  input[type='checkbox']:checked:disabled + label {
    cursor: not-allowed;

    &::before {
      @apply tw-bg-white;
    }

    &::after {
      @apply tw-bg-neutral-200;
      @apply tw-border-neutral-200;
    }

    .ndsInputCheckbox__check {
      &::v-deep .nds-icon {
        @apply !tw-stroke-neutral-500;
      }
    }
  }
}
</style>
